import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { deviceApi } from '../lib/api';
import i18n from '../i18n';
import { LANGUAGE_CODES, setDefaultLangCode } from '../i18n/languages';
import { collectDeviceInfo } from '../lib/device';
import {
  getSessionUser, setSession, clearSession,
  getDeviceToken, setDeviceToken, clearDeviceToken,
} from '../lib/authStorage';

const AuthContext = createContext(null);

// Operator UI language: explicit personal override wins, else the activity's
// language set by the Gestore, else Italian.
const applyOperatorLanguage = (u) => {
  if (u && u.restaurant_language) setDefaultLangCode(u.restaurant_language);
  const override = localStorage.getItem('rmp_ui_lang');
  const lang = (LANGUAGE_CODES.includes(override) && override)
    || (u && u.restaurant_language)
    || 'it';
  if (LANGUAGE_CODES.includes(lang)) i18n.changeLanguage(lang);
};

// When we learn the restaurant's default language (before any profile login),
// remember it and, unless the operator picked a language on this device, present
// the system in that default language right away (login screen included).
const applyDefaultLanguage = (restaurant) => {
  const lang = restaurant && restaurant.language;
  if (!LANGUAGE_CODES.includes(lang)) return;
  setDefaultLangCode(lang);
  const loginChoice = localStorage.getItem('rmp_login_lang');
  const uiOverride = localStorage.getItem('rmp_ui_lang');
  if (!LANGUAGE_CODES.includes(loginChoice) && !LANGUAGE_CODES.includes(uiOverride)) {
    i18n.changeLanguage(lang);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getSessionUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) applyOperatorLanguage(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Device (first login) ----
  const hasDevice = () => !!getDeviceToken();

  const getDeviceState = async () => {
    const { data } = await deviceApi.get('/auth/device/state');
    if (data && data.restaurant) applyDefaultLanguage(data.restaurant);
    return data; // { status: 'first_login'|'license_blocked'|'ok', restaurant? }
  };

  const unlockDevice = async ({ email, password }) => {
    const deviceInfo = await collectDeviceInfo();
    const { data } = await api.post('/auth/device/unlock', { email, password, ...deviceInfo });
    setDeviceToken(data.device_token);
    if (data && data.restaurant) applyDefaultLanguage(data.restaurant);
    return data; // { device_token, restaurant }
  };

  const resetDevice = () => {
    clearDeviceToken();
    clearSession();
    setUser(null);
  };

  // ---- Profiles (second login) ----
  const getProfiles = async () => {
    const { data } = await deviceApi.get('/auth/device/profiles');
    return data; // [{ id, name, username, role }]
  };

  const sessionLogin = async ({ profileId, password, pin }) => {
    const { data } = await deviceApi.post('/auth/session/login', {
      profile_id: profileId, password, pin,
    });
    setSession(data.access_token, data.user);
    setUser(data.user);
    applyOperatorLanguage(data.user);
    return data.user;
  };

  const logout = () => {
    clearSession();
    setUser(null);
    window.location.href = '/login';
  };

  const hasPerm = (perm) => {
    if (!user) return false;
    return (user.permissions || []).includes(perm);
  };

  const setUiLanguage = (code) => {
    if (!LANGUAGE_CODES.includes(code)) return;
    localStorage.setItem('rmp_ui_lang', code);
    i18n.changeLanguage(code);
  };

  return (
    <AuthContext.Provider value={{
      user, loading, setLoading, hasPerm, setUiLanguage,
      hasDevice, getDeviceState, unlockDevice, resetDevice,
      getProfiles, sessionLogin, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
